<template>
  <NavBar />

  <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="4000">
    {{ snackbarMessage }}
  </v-snackbar>

  <v-container fluid style="margin-top: 185px;">
    <!-- Header with SIM info -->
    <v-row class="mb-4">
      <v-col cols="12">
        <div class="d-flex align-center">
          <v-btn icon variant="text" size="small" @click="$router.back()" class="mr-2">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <div>
            <h1 class="text-h4">Servicii Retea SIM</h1>
            <div v-if="simInfo" class="text-body-2 text-grey-darken-1 mt-1">
              <v-chip size="small" class="mr-2" color="primary">SIM #{{ simId }}</v-chip>
              <v-chip size="small" class="mr-2">{{ simInfo.serial || '-' }}</v-chip>
              <v-chip size="small" class="mr-2" color="success">{{ simInfo.msisdn || '-' }}</v-chip>
              <v-chip size="small" :color="simInfo.sim_status === 'active' ? 'success' : 'warning'">
                {{ simInfo.sim_status }}
              </v-chip>
              <v-chip v-if="simInfo.mvno_pending" size="small" color="orange" class="ml-2">
                <v-icon start size="small">mdi-clock-outline</v-icon>
                MVNO Pending
              </v-chip>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>

    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4" />

    <template v-if="!loading && simInfo">
      <!-- Main Services (P-codes) - read-only info -->
      <v-card variant="outlined" class="mb-4">
        <v-toolbar color="teal" density="compact">
          <v-icon class="ml-4">mdi-cellphone-wireless</v-icon>
          <v-toolbar-title>Servicii Principale (informatii)</v-toolbar-title>
        </v-toolbar>
        <v-card-text>
          <v-row>
            <v-col v-for="svc in mainServices" :key="svc.name" cols="12" sm="6" md="3" lg="2">
              <v-chip
                :color="svc.active ? 'teal' : 'grey-lighten-2'"
                :variant="svc.active ? 'flat' : 'outlined'"
                class="ma-1"
                size="default"
              >
                <v-icon start size="small">{{ svc.active ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
                <strong>{{ svc.name }}</strong>
                <span class="ml-1 text-caption">{{ svc.description }}</span>
              </v-chip>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Roaming Panel (Zone 1 / Zone 2) -->
      <SimRoamingPanel
        :sim-id="simId"
        :roaming-active="simInfo.roaming_active || false"
        :current-roaming-zone="simInfo.current_roaming_zone"
        :countries-zone1="simInfo.countries_zone_1 || ''"
        :countries-zone2="simInfo.countries_zone_2 || ''"
        :mvno-pending="simInfo.mvno_pending || false"
        @roaming-changed="onRoamingChanged"
      />

      <!-- International/Supplementary Panel (S-codes) -->
      <SimInternationalPanel
        :sim-id="simId"
        :services="services"
        :mvno-pending="simInfo.mvno_pending || false"
        @service-toggled="onServiceToggled"
      />
    </template>

    <!-- Not active warning -->
    <v-alert v-if="!loading && simInfo && simInfo.sim_status !== 'active'" type="warning" class="mt-4">
      SIM-ul nu este activ. Serviciile de retea pot fi modificate doar pe SIM-uri active.
    </v-alert>
  </v-container>
</template>

<script>
import { defineComponent } from 'vue'
import NavBar from '@/components/NavBar.vue'
import SimRoamingPanel from '@/components/sim/SimRoamingPanel.vue'
import SimInternationalPanel from '@/components/sim/SimInternationalPanel.vue'
import { getSimNetworkServices } from '@/composables/useNetworkServicesAPI'
import store from '@/store'

export default defineComponent({
  name: 'SimNetworkServicesView',
  components: { NavBar, SimRoamingPanel, SimInternationalPanel },
  data() {
    return {
      simId: null,
      loading: false,
      simInfo: null,
      services: [],
      // Snackbar
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      // Auto-refresh timer
      refreshTimer: null
    }
  },
  computed: {
    mainServices() {
      return this.services.filter(s => s.category === 'main')
    }
  },
  methods: {
    async loadServices() {
      this.loading = true
      const token = store.getters.getToken
      const result = await getSimNetworkServices(token, this.simId)

      if (!result.error) {
        this.simInfo = {
          sim_id: result.sim_id,
          sim_status: result.sim_status,
          subscriber_id: result.subscriber_id,
          subscriberID: result.subscriberID,
          serial: result.serial,
          msisdn: result.msisdn,
          mvno_pending: result.mvno_pending,
          mvno_pending_command_id: result.mvno_pending_command_id,
          roaming_type_id: result.roaming_type_id,
          roaming_active: result.roaming_active,
          current_roaming_zone: result.current_roaming_zone,
          countries_zone_1: result.countries_zone_1,
          countries_zone_2: result.countries_zone_2
        }
        this.services = result.services || []

        // If mvno_pending, start auto-refresh to detect callback
        if (result.mvno_pending) {
          this.startAutoRefresh()
        } else {
          this.stopAutoRefresh()
        }
      } else {
        this.notify(result.message || 'Eroare la incarcarea serviciilor', 'error')
      }
      this.loading = false
    },

    onRoamingChanged({ error, message }) {
      if (error) {
        this.notify(message || 'Eroare', 'error')
      } else {
        this.notify(message || 'Comanda roaming trimisa', 'success')
      }
      this.loadServices()
    },

    onServiceToggled({ productCode, active, error, message }) {
      if (error) {
        this.notify(message || 'Eroare', 'error')
      } else {
        this.notify(message || `${productCode} ${active ? 'activat' : 'dezactivat'}`, 'success')
      }
      this.loadServices()
    },

    notify(message, color = 'success') {
      this.snackbarMessage = message
      this.snackbarColor = color
      this.showSnackbar = true
    },

    startAutoRefresh() {
      if (this.refreshTimer) return
      this.refreshTimer = setInterval(() => {
        this.loadServices()
      }, 5000)
    },

    stopAutoRefresh() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer)
        this.refreshTimer = null
      }
    }
  },
  created() {
    this.simId = this.$route.params.id
    this.loadServices()
  },
  beforeUnmount() {
    this.stopAutoRefresh()
  }
})
</script>
